/**
 * Per-signer color palette for the signature placement UX.
 *
 * Each signer in a SigningRequest is assigned a color slot by their
 * position in the signers array (deterministic and stable for the
 * lifetime of the session). The same color shows up everywhere that
 * signer's identity needs to be communicated:
 *   - field marker outline + bg (suggested state)
 *   - sidebar legend swatch
 *   - active-signer chip in the AddFieldToolbar
 *
 * The first two slots are semantic in the owner-send flow: slot 0 is the
 * document owner/self (green), slot 1 is the primary recipient (blue). For
 * fields assigned to another party, confirmed keeps that party's hue
 * (`otherPartyVetted*`) so sender and recipient cues stay aligned.
 */

export interface SignerColor {
  /** Display id, mainly for testing / a11y labels */
  id: string;
  /** Outline + bracket color for suggested-state markers */
  outline: string;
  /** Background fill for suggested-state markers */
  bg: string;
  /** Outline color once user has dragged or explicitly confirmed */
  confirmedOutline: string;
  /** Background fill for confirmed markers */
  confirmedBg: string;
  /**
   * Configure-mode: owner vetted placement for another party's field.
   * Keeps the signer's hue instead of the green "self confirmed" color.
   */
  otherPartyVettedOutline: string;
  otherPartyVettedBg: string;
}

/**
 * 9-color palette tuned for orange-brand contrast + WCAG AA on white
 * page backgrounds. Slots 0 and 1 intentionally map to self/recipient
 * in the signature review flow.
 */
export const SIGNER_PALETTE: SignerColor[] = [
  { id: 'owner-green', outline: '#1B7A4B', bg: '#F4FAF6', confirmedOutline: '#1B7A4B', confirmedBg: '#F4FAF6', otherPartyVettedOutline: '#1B7A4B', otherPartyVettedBg: '#E5F4EA' },
  // Slots 1+: confirmed markers keep this signer's hue (blue for primary recipient, etc.) — never reuse owner green.
  { id: 'azure',  outline: '#3F7CCC', bg: '#EEF4FC', confirmedOutline: '#2E5FA0', confirmedBg: '#E3EBFA', otherPartyVettedOutline: '#2E5FA0', otherPartyVettedBg: '#E3EBFA' },
  { id: 'amber',  outline: '#D46A3A', bg: '#FEF3EC', confirmedOutline: '#B8572E', confirmedBg: '#FCE8DE', otherPartyVettedOutline: '#B8572E', otherPartyVettedBg: '#FCE8DE' },
  { id: 'plum',   outline: '#8B4F9E', bg: '#F4ECF7', confirmedOutline: '#6D3D7D', confirmedBg: '#EDE4F2', otherPartyVettedOutline: '#6D3D7D', otherPartyVettedBg: '#EDE4F2' },
  { id: 'teal',   outline: '#2A8C8C', bg: '#E6F4F4', confirmedOutline: '#1F6B6B', confirmedBg: '#DDEFEF', otherPartyVettedOutline: '#1F6B6B', otherPartyVettedBg: '#DDEFEF' },
  { id: 'rose',   outline: '#C84F6E', bg: '#FBECF0', confirmedOutline: '#A33D56', confirmedBg: '#F5E4E9', otherPartyVettedOutline: '#A33D56', otherPartyVettedBg: '#F5E4E9' },
  { id: 'olive',  outline: '#7A8C2C', bg: '#F2F4E4', confirmedOutline: '#5F6D22', confirmedBg: '#EEF0DC', otherPartyVettedOutline: '#5F6D22', otherPartyVettedBg: '#EEF0DC' },
  { id: 'slate',  outline: '#5A6B7C', bg: '#EEF1F4', confirmedOutline: '#445566', confirmedBg: '#E5E9EE', otherPartyVettedOutline: '#445566', otherPartyVettedBg: '#E5E9EE' },
  { id: 'rust',   outline: '#A04A2C', bg: '#F8ECE6', confirmedOutline: '#7D3A22', confirmedBg: '#F3E4DC', otherPartyVettedOutline: '#7D3A22', otherPartyVettedBg: '#F3E4DC' },
];

/**
 * Look up a signer's color by their position in the signers array.
 * Wraps via modulo so >8 signers cycle back to the start (rare in
 * practice — 99% of contracts have ≤3 signers).
 */
export function colorForSigner(index: number): SignerColor {
  if (!Number.isFinite(index) || index < 0) return SIGNER_PALETTE[0];
  return SIGNER_PALETTE[index % SIGNER_PALETTE.length];
}
